import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import StationCard from "@/components/StationCard";
import SocialProof from "@/components/SocialProof";
import { useAuth } from "@/lib/auth";
import { Search, Sparkles, Globe2, Radio, Mic, BarChart3, Coins, Megaphone, FileText, Zap, ArrowUpRight, Crown, BookOpen } from "lucide-react";
import { Link } from "react-router-dom";

const HERO_BG = "https://images.unsplash.com/photo-1478737270239-2f02b77fc618?w=1600&q=80";

export default function Landing() {
  const { user } = useAuth();
  const [stations, setStations] = useState([]);
  const [catalog, setCatalog] = useState({ genres: [], countries: [], languages: [] });
  const [genre, setGenre] = useState("all");
  const [country, setCountry] = useState("GLOBAL");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  const fetchStations = async (params = {}) => {
    setLoading(true);
    try {
      const { data } = await api.get("/stations", { params });
      setStations(data || []);
    } catch (e) {
      console.warn("Failed to load stations:", e);
      setStations([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    (async () => {
      try { const c = await api.get("/catalog"); setCatalog(c.data); } catch (_) { /* catalog optional */ }
      await fetchStations();
    })();
  }, []);

  useEffect(() => {
    const params = {};
    if (genre !== "all") params.genre = genre;
    if (country !== "GLOBAL") params.country = country;
    if (q) params.q = q;
    fetchStations(params);
  }, [genre, country, q]);

  return (
    <main className="grain pb-32">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_BG} alt="" className="w-full h-full object-cover opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-b from-emerald-950/50 via-black/70 to-[#050505]" />
          <div className="absolute inset-0" style={{ backgroundImage: "radial-gradient(ellipse at top, rgba(16,185,129,0.15), transparent 60%)" }} />
        </div>
        <div className="relative max-w-7xl mx-auto px-5 pt-16 pb-24 sm:pt-24 sm:pb-32 grid lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-8">
            <div className="flex items-center gap-3 mb-6 flex-wrap">
              <span className="fl-mono text-[11px] tracking-[0.3em] uppercase text-emerald-500 flex items-center gap-2">
                <span className="live-dot" /> Worldwide · 24/7
              </span>
              <span className="fl-mono text-[11px] tracking-[0.3em] uppercase text-violet-400 border border-violet-400/30 rounded-full px-2 py-0.5" data-testid="hero-fm-plus-flip">
                FM + Flip (audiobooks)
              </span>
              <span className="fl-mono text-[11px] tracking-[0.3em] uppercase text-slate-500">Est. 2026</span>
            </div>
            <h1 className="fl-display text-5xl sm:text-7xl lg:text-[6.5rem] leading-[0.9] font-extrabold tracking-tighter">
              EVERYTHING YOU NEED
              <br />
              <span className="text-emerald-500">TO LAUNCH</span> A STATION.
            </h1>
            <p className="mt-6 max-w-2xl text-lg text-slate-300 leading-relaxed">
              Fliboe FM is the modern FM. Upload your tracks, schedule talk shows, and broadcast worldwide — synced for every listener, on any device. No transmitter required.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              {user ? (
                <Link to="/dashboard" className="fl-btn" data-testid="hero-launch-station">
                  <Mic className="w-4 h-4" /> Launch your station
                </Link>
              ) : (
                <Link to="/signup" className="fl-btn" data-testid="hero-launch-station">
                  <Mic className="w-4 h-4" /> Launch your station
                </Link>
              )}
              <a href="#discover" className="fl-btn ghost" data-testid="hero-listen-now">
                <Radio className="w-4 h-4" /> Listen now
              </a>
            </div>

            {/* Tuning dial */}
            <div className="mt-12 max-w-xl">
              <div className="dial" />
              <div className="flex justify-between fl-mono text-[10px] tracking-[0.25em] uppercase text-slate-500 mt-2">
                <span>88.1</span><span>94.5</span><span>101.7</span><span>108.0</span>
              </div>
            </div>
          </div>

          <div className="lg:col-span-4 hidden lg:block">
            <div className="fl-card p-6 bg-black/60 backdrop-blur">
              <div className="flex items-center gap-2 fl-mono text-[10px] tracking-[0.25em] uppercase text-emerald-500">
                <span className="live-dot" /> Now broadcasting from
              </div>
              <div className="mt-4 grid grid-cols-3 gap-3">
                {["LAGOS","NYC","TOKYO","SAO PAULO","NAIROBI","BERLIN","LONDON","LA","MUMBAI"].map(c => (
                  <span key={c} className="fl-mono text-[10px] tracking-widest uppercase text-slate-400 border border-white/10 rounded-md px-2 py-1.5 text-center">{c}</span>
                ))}
              </div>
              <div className="mt-6 flex items-end gap-1 h-14">
                {Array.from({length: 28}).map((_,i) => (
                  <span key={i} className="eq-bar" style={{ animationDelay: `${(i%6)*0.08}s`, height: `${10 + (i*7)%30}px` }} />
                ))}
              </div>
              <p className="mt-4 text-xs text-slate-400">Sync-locked across every listener. One frequency, one planet.</p>
            </div>
          </div>
        </div>
      </section>

      <SocialProof />

      {/* DISCOVER */}
      <section id="discover" className="max-w-7xl mx-auto px-5 -mt-10">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-6">
          <div className="min-w-0">
            <div className="fl-mono text-[10px] tracking-[0.3em] uppercase text-emerald-500 mb-2">// Tune in</div>
            <h2 className="fl-display text-2xl sm:text-4xl md:text-5xl font-extrabold leading-tight break-words" data-testid="discover-heading">DISCOVER STATIONS</h2>
          </div>
          <div className="relative w-full sm:w-80 shrink-0">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <input
              className="fl-input pl-9 w-full"
              placeholder="Search stations…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              data-testid="discover-search-input"
            />
          </div>
        </div>

        {/* Filters */}
        <div className="space-y-3 mb-8">
          <div className="flex items-start gap-2 flex-wrap">
            <span className="fl-mono text-[10px] tracking-[0.25em] uppercase text-slate-500 mt-2 mr-2"><Sparkles className="inline w-3 h-3 mr-1" /> Genre</span>
            <button onClick={() => setGenre("all")} className={`fl-pill ${genre === "all" ? "is-active" : ""}`} data-testid="filter-genre-all">All</button>
            {catalog.genres.map(g => (
              <React.Fragment key={g}>
                {g.toLowerCase() === "bollywood" && (
                  <Link
                    to="/flip"
                    className="fl-pill !border-violet-400/40 !text-violet-200 hover:!bg-violet-500/15 inline-flex items-center gap-1"
                    data-testid="filter-genre-audiobooks"
                  >
                    <BookOpen className="w-3 h-3" /> Audiobooks
                  </Link>
                )}
                <button onClick={() => setGenre(g)} className={`fl-pill ${genre === g ? "is-active" : ""}`} data-testid={`filter-genre-${g}`}>{g}</button>
              </React.Fragment>
            ))}
          </div>
          <div className="flex items-start gap-2 flex-wrap">
            <span className="fl-mono text-[10px] tracking-[0.25em] uppercase text-slate-500 mt-2 mr-2"><Globe2 className="inline w-3 h-3 mr-1" /> Region</span>
            {catalog.countries.map(c => (
              <button key={c} onClick={() => setCountry(c)} className={`fl-pill ${country === c ? "is-active" : ""}`} data-testid={`filter-country-${c}`}>{c}</button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5" data-testid="stations-loading">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="fl-card p-0 overflow-hidden animate-pulse">
                <div className="aspect-square bg-white/5" />
                <div className="p-4 space-y-2">
                  <div className="h-4 w-3/4 bg-white/10 rounded" />
                  <div className="h-3 w-1/2 bg-white/5 rounded" />
                </div>
              </div>
            ))}
          </div>
        ) : stations.length === 0 ? (
          <div className="text-slate-500 fl-mono text-xs tracking-widest uppercase py-12 text-center" data-testid="stations-empty">
            // No stations match{q ? ` "${q}"` : " your filters"}. Try a different search.
          </div>
        ) : (genre === "all" && country === "GLOBAL" && !q.trim()) ? (
          // ─── Curated home view: Featured hero + horizontal-scroll rows per genre ───
          (() => {
            // Sort stations by listener_count (deterministic floor already applied)
            const sorted = [...stations].sort((a, b) => (b.listener_count || 0) - (a.listener_count || 0));
            const featured = sorted.slice(0, 4);
            const rest = sorted.slice(4);
            // Group remaining by genre
            const groups = {};
            for (const s of rest) {
              const g = s.genre || "mixed";
              (groups[g] = groups[g] || []).push(s);
            }
            const genreOrder = Object.keys(groups).sort((a, b) => (groups[b].length - groups[a].length));
            return (
              <div className="space-y-12" data-testid="stations-curated">
                {/* ── FEATURED HERO ── */}
                {featured.length > 0 && (
                  <div>
                    <div className="flex items-baseline justify-between mb-4">
                      <div>
                        <div className="fl-mono text-[10px] tracking-[0.3em] uppercase text-emerald-500 mb-1 flex items-center gap-1.5">
                          <span className="live-dot" /> LIVE RIGHT NOW · TOP {featured.length}
                        </div>
                        <h3 className="fl-display text-2xl sm:text-3xl font-bold tracking-tight">Trending stations.</h3>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="stations-featured">
                      {featured.map(s => <StationCard key={s.station_id} station={s} />)}
                    </div>
                  </div>
                )}

                {/* ── GENRE ROWS (horizontal scroll) ── */}
                {genreOrder.map((g) => (
                  <div key={g} data-testid={`stations-row-${g}`}>
                    <div className="flex items-baseline justify-between mb-3">
                      <div>
                        <div className="fl-mono text-[10px] tracking-[0.3em] uppercase text-slate-500 mb-1">// GENRE</div>
                        <h3 className="fl-display text-xl sm:text-2xl font-bold tracking-tight capitalize">{g}</h3>
                      </div>
                      <button
                        onClick={() => setGenre(g)}
                        className="fl-mono text-[10px] tracking-[0.25em] uppercase text-emerald-500 hover:text-emerald-300 transition-colors"
                        data-testid={`row-see-all-${g}`}
                      >
                        See all →
                      </button>
                    </div>
                    <div className="relative -mx-5 px-5 overflow-x-auto pb-3 fl-scroll-row">
                      <div className="flex gap-4 min-w-max">
                        {groups[g].map(s => (
                          <div key={s.station_id} className="w-[240px] flex-shrink-0">
                            <StationCard station={s} />
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}

                <style>{`
                  .fl-scroll-row::-webkit-scrollbar { height: 6px; }
                  .fl-scroll-row::-webkit-scrollbar-track { background: transparent; }
                  .fl-scroll-row::-webkit-scrollbar-thumb { background: rgba(245,158,11,0.25); border-radius: 3px; }
                  .fl-scroll-row::-webkit-scrollbar-thumb:hover { background: rgba(245,158,11,0.5); }
                  .fl-scroll-row { scrollbar-width: thin; scrollbar-color: rgba(245,158,11,0.25) transparent; }
                `}</style>
              </div>
            );
          })()
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5" data-testid="stations-grid">
            {stations.map(s => <StationCard key={s.station_id} station={s} />)}
          </div>
        )}
      </section>

      {/* FEATURE QUICK-LINKS — 4 entry points above the value props */}
      <section className="max-w-7xl mx-auto px-5 pt-16">
        <div className="flex items-end justify-between mb-6">
          <div>
            <div className="fl-mono text-xs uppercase tracking-[0.3em] text-emerald-500">// What you can do on Fliboe FM</div>
            <h2 className="fl-display text-3xl sm:text-4xl font-bold tracking-tight mt-2">Pick your lane.</h2>
          </div>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4" data-testid="feature-quicklinks">
          {[
            {
              to: "/produce",
              icon: Sparkles,
              tag: "AI Shows",
              title: "AI-produced shows",
              desc: "Pro-quality narrated shows powered by Claude + voice AI.",
              testid: "feature-link-ai-shows",
              accent: "text-fuchsia-400",
              border: "hover:border-fuchsia-400/40",
            },
            {
              to: "/flip",
              icon: BookOpen,
              tag: "Fliboe Flip",
              title: "AI audiobooks",
              desc: "Indie authors. AI narration. 16 languages. $9.99/mo unlocks all.",
              testid: "feature-link-flip",
              accent: "text-violet-400",
              border: "hover:border-violet-400/40",
            },
            {
              to: "/pulse",
              icon: Zap,
              tag: "Pulse · SEO",
              title: "Auto-SEO blog",
              desc: "Rank on Google, Bing & ChatGPT with AI-written posts.",
              testid: "feature-link-pulse",
              accent: "text-emerald-400",
              border: "hover:border-emerald-400/40",
            },
            {
              to: "/advertise",
              icon: Megaphone,
              tag: "Advertise",
              title: "Local-business ads",
              desc: "Run 30-sec audio spots on stations — $25–35 per slot.",
              testid: "feature-link-advertise",
              accent: "text-emerald-400",
              border: "hover:border-emerald-400/40",
            },
            {
              to: "/pricing",
              icon: Crown,
              tag: "Plans",
              title: "Broadcaster plans",
              desc: "Free, Premium, and Broadcaster Pro — unlock more reach.",
              testid: "feature-link-plans",
              accent: "text-sky-400",
              border: "hover:border-sky-400/40",
            },
          ].map(({ to, icon: Icon, tag, title, desc, testid, accent, border }) => (
            <Link
              key={to}
              to={to}
              className={`fl-card p-5 group transition-colors ${border}`}
              data-testid={testid}
            >
              <div className="flex items-start justify-between">
                <Icon className={`w-6 h-6 ${accent}`} />
                <ArrowUpRight className="w-4 h-4 text-white/30 group-hover:text-white/80 transition-colors" />
              </div>
              <div className={`fl-mono text-[10px] uppercase tracking-[0.25em] mt-4 ${accent}`}>{tag}</div>
              <div className="text-white font-bold text-lg mt-1 leading-tight">{title}</div>
              <div className="text-white/55 text-sm mt-2 leading-relaxed">{desc}</div>
            </Link>
          ))}
        </div>
      </section>

      {/* VALUE PROPS */}
      <section className="max-w-7xl mx-auto px-5 py-24">
        <div className="grid md:grid-cols-3 gap-5">
          {[
            { icon: Mic, t: "Auto-DJ 24/7", d: "Upload your tracks, set an order, and Fliboe spins them on a synchronised global clock." },
            { icon: BarChart3, t: "Geo-demographic analytics", d: "See where listeners tune in from in real time, segment by region and age." },
            { icon: Coins, t: "Profit-ready", d: "Add your Tip Jar (PayPal, Cash App, Venmo) — 100% goes directly to you. Fliboe FM doesn't take a cut." },
          ].map(({ icon: Icon, t, d }) => (
            <div key={t} className="fl-card p-6">
              <Icon className="w-7 h-7 text-emerald-500" />
              <h3 className="fl-display mt-4 text-2xl font-bold tracking-tight">{t}</h3>
              <p className="mt-2 text-slate-400 leading-relaxed">{d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* FLIBOE FLIP — Audiobook publishing */}
      <section className="max-w-7xl mx-auto px-5 pb-24" data-testid="landing-flip-section">
        <div className="fl-card p-8 md:p-12 bg-gradient-to-br from-violet-500/10 via-black/40 to-fuchsia-500/10 border-violet-400/20">
          <div className="grid md:grid-cols-12 gap-8 items-center">
            <div className="md:col-span-7">
              <div className="flex items-center gap-3 mb-4">
                <BookOpen className="w-6 h-6 text-violet-400" />
                <div className="fl-mono text-[10px] uppercase tracking-[0.3em] text-violet-400">// A different product · Fliboe Flip</div>
              </div>
              <h2 className="fl-display text-4xl sm:text-5xl font-black text-white tracking-tighter leading-[0.95]">
                Indie audiobooks,<br />
                <span className="text-violet-400">AI-narrated.</span>
              </h2>
              <p className="mt-5 text-slate-300 text-lg leading-relaxed max-w-xl">
                Fliboe Flip is our audiobook side — separate from Fliboe FM radio. Upload a manuscript, pick a voice, and we narrate every chapter in 16 languages. Authors keep 100% of tips.
              </p>
              <div className="mt-5 flex flex-wrap gap-2">
                {["novels", "fiction", "sci-fi", "novella", "biography", "kids"].map(c => (
                  <span key={c} className="fl-mono text-[10px] uppercase tracking-widest text-violet-300/80 border border-violet-400/20 bg-violet-500/5 rounded-full px-3 py-1">
                    {c}
                  </span>
                ))}
              </div>
              <div className="mt-7 flex flex-wrap gap-3">
                <Link to="/flip" className="fl-btn" data-testid="landing-flip-browse">
                  <BookOpen className="w-4 h-4" /> Browse the catalog
                </Link>
                <Link to="/publish" className="fl-btn ghost" data-testid="landing-flip-publish">
                  <Sparkles className="w-4 h-4" /> Publish your book
                </Link>
              </div>
              <div className="mt-5 text-slate-500 text-xs fl-mono uppercase tracking-widest">
                First chapter free · $9.99/mo unlocks the library
              </div>
            </div>
            <div className="md:col-span-5">
              <div className="grid grid-cols-3 gap-2">
                {Array.from({ length: 9 }).map((_, i) => (
                  <div
                    key={i}
                    className="aspect-[3/4] rounded-md border border-white/10 bg-gradient-to-br from-violet-500/20 via-fuchsia-500/10 to-black/40 flex items-center justify-center"
                  >
                    <BookOpen className="w-6 h-6 text-white/25" />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
